import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';

import { DirectivesModule } from 'app/src/common/directives.module';
import { ContainerModule } from 'app/src/containers/container.module';
import { FileModule } from 'app/src/files/file.module';
import { MaterialModule } from 'app/src/material.module';
import { PipesModule } from 'app/src/shared/pipes.module';
import { SharedModule } from 'app/src/shared/shared.module';

import { ProjectsService } from '../../projects/projects.service';
import { getContainerProjectId } from '../../shared/container.utils';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { ContainerInfoValue } from '../../shared/models/container.model';
import { Project } from '../../shared/models/project.model';
import { Session, SessionBidsError } from '../../shared/models/session.model';
import { yieldClassProvider } from '../../shared/provider.utils';
import { SidebarService } from '../../sidebar.service';
import { AcquisitionListComponent } from '../acquisition-list/acquisition-list.component';
import { BidsFilesComponent } from '../bids-files/bids-files.component';
import { getSessionBidsError } from '../session.utils';
import { SessionDataState } from './session-data.state';

export { AcquisitionFilters, SessionDataState } from './session-data.state';

export interface SessionActions {
  viewSessionTemplates: boolean;
}

@Component({
  host: { 'test-id': 'session-data' },
  selector: 'session-data',
  templateUrl: './session-data.component.html',
  styleUrls: ['./session-data.component.scss'],
  imports: [
    CommonModule,
    ContainerModule,
    DirectivesModule,
    DragDropModule,
    FileModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    PipesModule,
    SharedModule,
    RouterModule,
    BidsFilesComponent,
    AcquisitionListComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    // If SessionDataState is not provided by a parent, provide our own
    yieldClassProvider(SessionDataState),
  ],
})
export class SessionDataComponent implements OnChanges, OnDestroy {
  private projectsService = inject(ProjectsService);
  private sidebar = inject(SidebarService);
  private state = inject(SessionDataState);

  @Input() collectionId?: string; // passed to acquisition-list
  @Input() project: Project;
  @Input() session: Session;

  bidsView$ = this.sidebar.bidsViewActive$.asObservable();
  permissions: SessionActions = {
    viewSessionTemplates: false,
  };
  projectId$ = new Subject<string>();
  sessionError: SessionBidsError = null;

  private subscriptions = {
    permissions: Subscription.EMPTY,
  };

  constructor() {
    this.subscriptions.permissions = this.projectId$
      .pipe(
        distinctUntilChanged(),
        switchMap((projectId) => this.projectsService.getProjectPermissions(projectId)),
        map((permissions) => new Set(permissions)),
      )
      .subscribe((permissions) => {
        this.state.projectPermissions$.next(permissions);
        this.permissions = {
          viewSessionTemplates: permissions.has(PermissionAction.SessionTemplatesView),
        };
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.session && this.session?._id) {
      this.sessionError = getSessionBidsError(this.session);
      this.projectId$.next(getContainerProjectId(this.session));
    }
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  get projectBids(): ContainerInfoValue {
    return this.project?.info?.BIDS;
  }
}
